import * as React from 'react';
import { getMouseCanvas, GenericChartComponent } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';
import { roundedRect } from '@/utils/canvasUtils';

interface LabelProps {
  readonly bgFillStyle: string;
  readonly borderRadius?: number;
  readonly textFill: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontWeight: number | string;
  readonly fontStyle: string;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly text: string;
  readonly textBox: {
    readonly closeIcon?: unknown;
    readonly right: number;
    readonly height: number;
    readonly padding: {
      left: number;
      right: number;
    };
  };
  readonly yValue: number;

  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly interactiveCursorClass?: string;
  readonly selected: boolean;
  readonly corners?: 'all' | 'left' | 'right';
  readonly strokeSides?: 'all' | 'no-right';
}

export class TPAndSLDragHandle extends React.Component<LabelProps> {
  static defaultProps = {
    selected: false,
    borderRadius: 0,
  };

  public width = 0;

  render() {
    const { onHover, onUnHover, onDragComplete, onDrag, onDragStart, onClick, interactiveCursorClass, onDoubleClick } =
      this.props;

    return (
      <GenericChartComponent
        // clip={true}: 핸들 박스가 차트 경계 근처일 때 박스 위/아래가 서브차트 영역으로
        // 삐져나가는 현상 방지. 핸들 x 위치는 차트 패널 내부이므로 clip 해도 무방.
        // (GenericComponent defaultProps.clip === true 이지만 의도 명시 위해 true 지정)
        clip={true}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        isHover={this.isHover}
        enableDragOnHover={!!(onDragStart || onDrag || onDragComplete)}
        interactiveCursorClass={interactiveCursorClass}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onHover={onHover}
        onUnHover={onUnHover}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    ctx.save();
    const {
      bgFillStyle,
      borderRadius,
      textFill,
      fontFamily,
      fontSize,
      fontStyle,
      fontWeight,
      text,
      textBox,
      strokeStyle,
      yValue,
      strokeWidth,
    } = this.props;

    const {
      chartConfig: { width, yScale, height },
    } = moreProps;

    const y = Math.round(yScale(yValue));
    if (y < 0 || y > height) {
      ctx.restore();
      return;
    }

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;

    const textWidth = ctx.measureText(text).width;
    const totalWidth = textBox.padding.left + textWidth + textBox.padding.right;

    this.width = totalWidth;

    const rect = {
      x: width - this.width - textBox.right,
      y: y - textBox.height / 2,
      height: textBox.height,
    };

    const r = borderRadius ?? 0;
    // corners: 'left'=오른쪽 radius 0, 'right'=왼쪽 radius 0, 'all'=전체
    const corners = this.props.corners ?? 'all';
    const tl = corners === 'right' ? 0 : r;
    const bl = corners === 'right' ? 0 : r;

    // draw background
    ctx.fillStyle = bgFillStyle;
    roundedRect(ctx, rect.x, rect.y, this.width, rect.height, r, corners);
    ctx.fill();

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeStyle;
    if (this.props.strokeSides === 'no-right') {
      // 오른쪽 면 제외: 상단 → 좌상 → 좌하 → 하단 (열린 path)
      const boxX = rect.x;
      const boxY = rect.y;
      const boxW = this.width;
      const boxH = rect.height;
      ctx.beginPath();
      ctx.moveTo(boxX + boxW, boxY); // 우상 (열린 시작)
      ctx.lineTo(boxX + tl, boxY); // 상단
      ctx.arcTo(boxX, boxY, boxX, boxY + tl, tl); // 좌상 곡선
      ctx.lineTo(boxX, boxY + boxH - bl); // 좌측
      ctx.arcTo(boxX, boxY + boxH, boxX + bl, boxY + boxH, bl); // 좌하 곡선
      ctx.lineTo(boxX + boxW, boxY + boxH); // 하단
      ctx.stroke();
    } else {
      roundedRect(ctx, rect.x, rect.y, this.width, rect.height, r, corners);
      ctx.stroke();
    }

    ctx.fillStyle = textFill;
    ctx.fillText(text, rect.x + this.width / 2, y);
    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    const { onHover } = this.props;
    if (!onHover) return false;
    // drawOnCanvas가 아직 실행되지 않은 초기 상태 — 좌표가 유효하지 않으므로 히트 테스트 불가
    if (this.width === 0) return false;

    const { yValue, textBox } = this.props;
    const {
      chartConfig: { width, yScale },
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(yValue));
    const rect = {
      x: width - this.width - textBox.right,
      y: y - textBox.height / 2,
      height: textBox.height,
    };

    return mouseX >= rect.x && mouseX <= rect.x + this.width && mouseY >= rect.y && mouseY <= rect.y + rect.height;
  };
}
